// Modal that hosts the full materials catalogue with existing filters, search, and selection callbacks.
import React from "react";
import { Matiere } from "../types";
import { matiereService } from "../../../../services/MatiereService";

interface MaterialsCatalogModalProps {
  isOpen: boolean;
  onClose: () => void;
  matieres: Matiere[];
  matieresFiltered: Matiere[];
  filtresActifs: boolean;
  filtreRecherche: string;
  setFiltreRecherche: (v: string) => void;
  filtreType: string;
  setFiltreType: (v: string) => void;
  filtreLaizeMin: string;
  setFiltreLaizeMin: (v: string) => void;
  filtreLaizeMax: string;
  setFiltreLaizeMax: (v: string) => void;
  filtreUsage: {
    indoor: boolean;
    outdoor: boolean;
    nautisme: boolean;
    medical: boolean;
  };
  setFiltreUsage: React.Dispatch<
    React.SetStateAction<{
      indoor: boolean;
      outdoor: boolean;
      nautisme: boolean;
      medical: boolean;
    }>
  >;
  filtreCouleur: string;
  setFiltreCouleur: (v: string) => void;
  filtreFournisseur: { [key: string]: boolean };
  setFiltreFournisseur: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
  typesUniques: string[];
  couleursUniques: string[];
  fournisseursUniques: (string | undefined)[];
  hasUsageData: boolean;
  laizeMin?: number;
  laizeMax?: number;
  reinitialiserFiltres: () => void;
  favorisLocaux: { [key: string]: boolean };
  toggleFavori: (id: string, e: React.MouseEvent) => void;
  onSelectMaterial: (id: string, defaultColor?: string, matiereObj?: Matiere) => void;
  selectedMaterialId?: string;
  onMatieresRefresh?: (matieres: Matiere[]) => void;
}

const MaterialsCatalogModal: React.FC<MaterialsCatalogModalProps> = ({
  isOpen,
  onClose,
  matieres,
  matieresFiltered,
  filtresActifs,
  filtreRecherche,
  setFiltreRecherche,
  filtreType,
  setFiltreType,
  filtreLaizeMin,
  setFiltreLaizeMin,
  filtreLaizeMax,
  setFiltreLaizeMax,
  filtreUsage,
  setFiltreUsage,
  filtreCouleur,
  setFiltreCouleur,
  filtreFournisseur,
  setFiltreFournisseur,
  typesUniques,
  couleursUniques,
  fournisseursUniques,
  hasUsageData,
  laizeMin,
  laizeMax,
  reinitialiserFiltres,
  favorisLocaux,
  toggleFavori,
  onSelectMaterial,
  selectedMaterialId,
  onMatieresRefresh,
}) => {
  const [showAdvanced, setShowAdvanced] = React.useState(false);
  const initialCreateForm = {
    nom: "",
    laize: "",
    prix: "",
    remiseFournisseur: "",
    minimumCommande: "",
    couleurInput: "",
  };
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [createForm, setCreateForm] = React.useState(initialCreateForm);
  const [createColors, setCreateColors] = React.useState<string[]>([]);
  const [createErrors, setCreateErrors] = React.useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [creationError, setCreationError] = React.useState<string | null>(null);
  const searchRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [isOpen]);

  const resetCreateForm = () => {
    setCreateForm(initialCreateForm);
    setCreateColors([]);
    setCreateErrors({});
    setCreationError(null);
  };

  const handleAddColor = () => {
    const value = createForm.couleurInput.trim();
    if (!value) {
      setCreateErrors((prev) => ({ ...prev, couleurs: "La couleur ne peut pas être vide" }));
      return;
    }

    const exists = createColors.some((c) => c.toLowerCase() === value.toLowerCase());
    if (exists) {
      setCreateErrors((prev) => ({ ...prev, couleurs: "Couleur déjà ajoutée" }));
      setCreateForm((prev) => ({ ...prev, couleurInput: "" }));
      return;
    }

    setCreateColors((prev) => [...prev, value]);
    setCreateForm((prev) => ({ ...prev, couleurInput: "" }));
    setCreateErrors((prev) => {
      const next = { ...prev };
      delete next.couleurs;
      return next;
    });
  };

  const handleRemoveColor = (color: string) => {
    setCreateColors((prev) => prev.filter((c) => c !== color));
  };

  const validateCreateForm = () => {
    const errors: Record<string, string> = {};
    const nom = createForm.nom.trim();
    const laize = parseFloat(createForm.laize);
    const prix = parseFloat(createForm.prix);
    const remise = parseFloat(createForm.remiseFournisseur);
    const minimumCommande = parseFloat(createForm.minimumCommande);

    if (!nom) errors.nom = "Nom obligatoire";
    if (isNaN(laize) || laize <= 0) errors.laize = "Laize doit être > 0";
    if (isNaN(prix) || prix <= 0) errors.prix = "Prix doit être > 0";
    if (isNaN(remise) || remise < 0 || remise > 100) errors.remiseFournisseur = "Remise entre 0 et 100";
    if (isNaN(minimumCommande) || minimumCommande <= 0) {
      errors.minimumCommande = "Minimum > 0";
    } else if (minimumCommande < 0.1) {
      errors.minimumCommande = "Minimum ≥ 0.1";
    }
    if (createColors.length === 0) errors.couleurs = "Ajoutez au moins une couleur";

    setCreateErrors(errors);

    return {
      isValid: Object.keys(errors).length === 0,
      nom,
      laize,
      prix,
      remise,
      minimumCommande,
    };
  };

  const handleSubmitCreate = async () => {
    setCreationError(null);
    const { isValid, nom, laize, prix, remise, minimumCommande } = validateCreateForm();
    if (!isValid) return;

    setIsSubmitting(true);
    try {
      const newMatiere = await matiereService.saveMatiere({
        nom,
        laize,
        prix,
        remiseFournisseur: remise,
        minimumCommande,
        couleurs: createColors,
      } as Matiere);

      const refreshed = await matiereService.getMatieres();
      onMatieresRefresh?.(refreshed);

      const defaultColor = createColors.length === 1 ? createColors[0] : undefined;

      resetCreateForm();
      setIsCreateOpen(false);
      onSelectMaterial(newMatiere.id, defaultColor, newMatiere);
    } catch (error) {
      console.error("Erreur lors de la création de la matière:", error);
      setCreationError("Erreur lors de la création de la matière. Merci de réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-30 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl mt-6">
        <div className="flex items-start justify-between p-4 border-b">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Catalogue matières</h3>
            <p className="text-sm text-gray-500">Recherche, filtres complets et liste exhaustive</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-sm">
            Fermer
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => {
                resetCreateForm();
                setIsCreateOpen((prev) => !prev);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100"
            >
              <span className="text-lg" aria-hidden>
                +
              </span>
              Nouvelle matière
            </button>
          </div>

          {isCreateOpen && (
            <div className="border border-blue-100 bg-blue-50/60 rounded-lg p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Nom de la matière</label>
                  <input
                    type="text"
                    value={createForm.nom}
                    onChange={(e) => {
                      setCreateForm((prev) => ({ ...prev, nom: e.target.value }));
                      setCreateErrors((prev) => {
                        const next = { ...prev };
                        delete next.nom;
                        return next;
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: Tissu premium"
                  />
                  {createErrors.nom && <p className="text-xs text-red-600 mt-1">{createErrors.nom}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Laize (cm)</label>
                  <input
                    type="number"
                    min="0"
                    value={createForm.laize}
                    onChange={(e) => {
                      setCreateForm((prev) => ({ ...prev, laize: e.target.value }));
                      setCreateErrors((prev) => {
                        const next = { ...prev };
                        delete next.laize;
                        return next;
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: 150"
                  />
                  {createErrors.laize && <p className="text-xs text-red-600 mt-1">{createErrors.laize}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Prix public TTC / ML (€)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={createForm.prix}
                    onChange={(e) => {
                      setCreateForm((prev) => ({ ...prev, prix: e.target.value }));
                      setCreateErrors((prev) => {
                        const next = { ...prev };
                        delete next.prix;
                        return next;
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: 25.50"
                  />
                  {createErrors.prix && <p className="text-xs text-red-600 mt-1">{createErrors.prix}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Remise fournisseur (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={createForm.remiseFournisseur}
                    onChange={(e) => {
                      setCreateForm((prev) => ({ ...prev, remiseFournisseur: e.target.value }));
                      setCreateErrors((prev) => {
                        const next = { ...prev };
                        delete next.remiseFournisseur;
                        return next;
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: 50"
                  />
                  {createErrors.remiseFournisseur && (
                    <p className="text-xs text-red-600 mt-1">{createErrors.remiseFournisseur}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Minimum à la commande (ML)</label>
                  <input
                    type="number"
                    min="0.1"
                    step="0.1"
                    value={createForm.minimumCommande}
                    onChange={(e) => {
                      setCreateForm((prev) => ({ ...prev, minimumCommande: e.target.value }));
                      setCreateErrors((prev) => {
                        const next = { ...prev };
                        delete next.minimumCommande;
                        return next;
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: 1"
                  />
                  {createErrors.minimumCommande && (
                    <p className="text-xs text-red-600 mt-1">{createErrors.minimumCommande}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Couleurs disponibles</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={createForm.couleurInput}
                    onChange={(e) => {
                      setCreateForm((prev) => ({ ...prev, couleurInput: e.target.value }));
                      setCreateErrors((prev) => {
                        const next = { ...prev };
                        delete next.couleurs;
                        return next;
                      });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddColor();
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: Blanc"
                  />
                  <button
                    type="button"
                    onClick={handleAddColor}
                    className="px-3 py-2 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700"
                  >
                    Ajouter
                  </button>
                </div>
                {createErrors.couleurs && <p className="text-xs text-red-600">{createErrors.couleurs}</p>}
                <div className="flex flex-wrap gap-2">
                  {createColors.map((color) => (
                    <span
                      key={color}
                      className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-xs"
                    >
                      {color}
                      <button
                        type="button"
                        className="text-blue-600 hover:text-blue-800"
                        onClick={() => handleRemoveColor(color)}
                        aria-label={`Supprimer ${color}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {creationError && <div className="text-sm text-red-600">{creationError}</div>}

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className="px-4 py-2 rounded-md border border-gray-300 text-sm text-gray-700 bg-white hover:bg-gray-50"
                  onClick={() => {
                    resetCreateForm();
                    setIsCreateOpen(false);
                  }}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-60"
                  onClick={handleSubmitCreate}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Création..." : "Créer et utiliser"}
                </button>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <input
              ref={searchRef}
              type="text"
              placeholder="Recherche par nom de matière"
              value={filtreRecherche}
              onChange={(e) => setFiltreRecherche(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                <select
                  value={filtreType}
                  onChange={(e) => setFiltreType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tous les types</option>
                  {typesUniques.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Laize (cm) {laizeMin && laizeMax && <span className="text-gray-400">({laizeMin}-{laizeMax})</span>}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    value={filtreLaizeMin}
                    onChange={(e) => setFiltreLaizeMin(e.target.value)}
                    min="0"
                    className="w-full px-2 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    type="number"
                    placeholder="Max"
                    value={filtreLaizeMax}
                    onChange={(e) => setFiltreLaizeMax(e.target.value)}
                    min="0"
                    className="w-full px-2 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Fournisseur</label>
                <div className="flex items-center justify-between gap-2">
                  <select
                    value=""
                    onChange={(e) => {
                      const value = e.target.value;
                      setFiltreFournisseur((prev) => ({ ...prev, [value]: !prev[value] }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Sélectionner</option>
                    {fournisseursUniques.map(
                      (fournisseur) =>
                        fournisseur && (
                          <option key={fournisseur} value={fournisseur}>
                            {fournisseur}
                          </option>
                        )
                    )}
                  </select>
                  <button
                    type="button"
                    onClick={() =>
                      setFiltreFournisseur((prev) => {
                        const all = { ...prev };
                        Object.keys(all).forEach((k) => (all[k] = false));
                        return all;
                      })
                    }
                    className="text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap"
                  >
                    Réinitialiser
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 mt-1">
                  {Object.entries(filtreFournisseur)
                    .filter(([, checked]) => checked)
                    .map(([fournisseur]) => (
                      <span
                        key={fournisseur}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200"
                      >
                        {fournisseur}
                        <button
                          type="button"
                          onClick={() => setFiltreFournisseur((prev) => ({ ...prev, [fournisseur]: false }))}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          ✕
                        </button>
                      </span>
                    ))}
                </div>
              </div>
            </div>

            <button
              type="button"
              className="text-sm text-blue-700 underline"
              onClick={() => setShowAdvanced((prev) => !prev)}
            >
              {showAdvanced ? "Masquer les filtres avancés" : "Filtres avancés"}
            </button>

            {showAdvanced && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
                {hasUsageData && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">Usage</label>
                    <div className="space-y-2 p-2 border border-gray-200 rounded-md bg-white">
                      <label className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={filtreUsage.indoor}
                          onChange={(e) => setFiltreUsage((prev) => ({ ...prev, indoor: e.target.checked }))}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Indoor</span>
                      </label>
                      <label className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={filtreUsage.outdoor}
                          onChange={(e) => setFiltreUsage((prev) => ({ ...prev, outdoor: e.target.checked }))}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Outdoor</span>
                      </label>
                      <label className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={filtreUsage.nautisme}
                          onChange={(e) => setFiltreUsage((prev) => ({ ...prev, nautisme: e.target.checked }))}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Nautisme</span>
                      </label>
                      <label className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={filtreUsage.medical}
                          onChange={(e) => setFiltreUsage((prev) => ({ ...prev, medical: e.target.checked }))}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Médical</span>
                      </label>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Couleur</label>
                  <select
                    value={filtreCouleur}
                    onChange={(e) => setFiltreCouleur(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Toutes</option>
                    {couleursUniques.map((couleur) => (
                      <option key={couleur} value={couleur}>
                        {couleur}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex items-end gap-3">
                  {filtresActifs && (
                    <button
                      type="button"
                      onClick={reinitialiserFiltres}
                      className="px-3 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Réinitialiser les filtres
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="text-xs text-gray-600">
            {filtresActifs ? (
              <span>
                <strong>{matieresFiltered.length}</strong> matière{matieresFiltered.length > 1 ? "s" : ""} trouvée
                {matieresFiltered.length !== matieres.length && (
                  <span className="text-gray-500"> sur {matieres.length} au total</span>
                )}
              </span>
            ) : (
              <span>
                {matieres.length} matière{matieres.length > 1 ? "s" : ""} disponible{matieres.length > 1 ? "s" : ""}
              </span>
            )}
          </div>

          {matieresFiltered.length === 0 && filtresActifs && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <div>
                  <p className="text-sm font-medium text-yellow-800">Aucune matière ne correspond à vos critères</p>
                  <p className="text-xs text-yellow-700 mt-1">Essayez de modifier ou réinitialiser les filtres</p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-1">
            {matieresFiltered.map((matiere) => {
              const isSelected = selectedMaterialId === matiere.id;
              return (
                <div
                  key={matiere.id}
                  className={`border rounded-lg p-3 cursor-pointer transition-all hover:shadow-md relative ${
                    isSelected ? "border-primary-500 bg-primary-50 ring-2 ring-primary-500 ring-opacity-50" : "border-gray-200 hover:border-primary-300"
                  }`}
                  onClick={() => {
                    onSelectMaterial(matiere.id);
                  }}
                >
                  <button
                    onClick={(e) => toggleFavori(matiere.id, e)}
                    className="absolute top-2 right-2 text-xl hover:scale-110 transition-transform z-10"
                    title={(favorisLocaux[matiere.id] ?? matiere.favori) ? "Retirer des favoris" : "Ajouter aux favoris"}
                  >
                    {(favorisLocaux[matiere.id] ?? matiere.favori) ? "★" : "☆"}
                  </button>

                  <div className="flex justify-between items-start pr-8">
                    <div className="flex-1">
                      <div className="font-medium text-gray-800">{matiere.nom}</div>
                      <div className="text-sm text-gray-500">
                        {matiere.type} · Laize {matiere.laize}cm
                      </div>
                      <div className="text-sm text-gray-500">
                        {(matiere.prix * 1.2).toFixed(2)}€ TTC/ML · {matiere.couleurs.length} coloris
                      </div>
                    </div>
                    {(matiere.usage_indoor || matiere.usage_outdoor || matiere.usage_nautisme || matiere.usage_medical) && (
                      <div className="flex flex-col gap-1 ml-2 text-[11px] text-gray-600">
                        {matiere.usage_indoor && <span title="Indoor">IND</span>}
                        {matiere.usage_outdoor && <span title="Outdoor">OUT</span>}
                        {matiere.usage_nautisme && <span title="Nautisme">NAUT</span>}
                        {matiere.usage_medical && <span title="Medical">MED</span>}
                      </div>
                    )}
                  </div>

                  {matiere.url_produit && (
                    <a
                      href={matiere.url_produit}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="absolute bottom-2 right-2 bg-blue-100 hover:bg-blue-200 rounded-full p-1.5 transition-all z-10 shadow-sm"
                      title="Voir la fiche fournisseur"
                    >
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaterialsCatalogModal;
